import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import {
  SpeedCompetitionData, Competitor, QualSlot, FinalsMatch,
  computeGenderedRankings, autoPopulateBracket, formatTime, getMatchWinner,
} from '../utils/speedCompetition';
import { subscribeSpeedData } from '../firebase/speedService';
import { ATHLETE_BY_NAME } from '../data/athletes';
import ClassBadge from '../components/ClassBadge';

// ─── helpers ──────────────────────────────────────────────────────────────────

function getName(id: string | null | undefined, competitors: Competitor[]): string {
  if (!id) return '';
  return competitors.find(c => c.id === id)?.name ?? id;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionHeader: React.FC<{ title: string; color: string }> = ({ title, color }) => (
  <div className={`${color} text-white px-5 py-3 rounded-t-lg`}>
    <h2 className="text-xl font-bold tracking-wide">{title}</h2>
  </div>
);

const QualTable: React.FC<{ slots: QualSlot[]; competitors: Competitor[]; title: string }> = ({
  slots, competitors, title,
}) => (
  <div className="mb-2">
    <h3 className="text-base font-semibold text-gray-600 uppercase tracking-wider mb-1 px-1">{title}</h3>
    <div className="overflow-x-auto">
      <table className="w-full text-base border-collapse">
        <thead>
          <tr className="bg-gray-100 text-gray-500 text-xs uppercase tracking-wide">
            <th className="px-4 py-2 text-left w-40">Time</th>
            <th className="px-4 py-2 text-left">Left Lane</th>
            <th className="px-4 py-2 text-center w-28">Time</th>
            <th className="px-4 py-2 text-center w-8 text-gray-300">vs</th>
            <th className="px-4 py-2 text-left">Right Lane</th>
            <th className="px-4 py-2 text-center w-28">Time</th>
          </tr>
        </thead>
        <tbody>
          {slots.map((slot, i) => {
            const leftDone  = slot.leftResult.time !== null  || slot.leftResult.dnf;
            const rightDone = slot.rightResult.time !== null || slot.rightResult.dnf;
            const bothDone  = leftDone && (rightDone || !slot.rightId);
            const leftName  = getName(slot.leftId,  competitors);
            const rightName = getName(slot.rightId, competitors);
            return (
              <tr
                key={i}
                className={`border-b border-gray-100 ${bothDone ? 'bg-green-50' : 'bg-white'} hover:bg-gray-50`}
              >
                <td className="px-4 py-3 font-mono text-sm text-gray-500 whitespace-nowrap">{slot.timeRange}</td>
                <td className="px-4 py-3 font-semibold text-dark">{leftName ? (
                  ATHLETE_BY_NAME[leftName]?.id
                    ? <Link to={`/athletes/${ATHLETE_BY_NAME[leftName].id}`} className="hover:text-primary hover:underline transition-colors">{leftName}</Link>
                    : leftName
                ) : <span className="text-gray-300 italic text-sm">bye</span>}</td>
                <td className="px-4 py-3 text-center font-mono font-bold text-primary">
                  {leftName ? formatTime(slot.leftResult) : ''}
                </td>
                <td className="px-4 py-3 text-center text-gray-300 text-sm">vs</td>
                <td className="px-4 py-3 font-semibold text-dark">{rightName ? (
                  ATHLETE_BY_NAME[rightName]?.id
                    ? <Link to={`/athletes/${ATHLETE_BY_NAME[rightName].id}`} className="hover:text-primary hover:underline transition-colors">{rightName}</Link>
                    : rightName
                ) : <span className="text-gray-300 italic text-sm">bye</span>}</td>
                <td className="px-4 py-3 text-center font-mono font-bold text-primary">
                  {rightName ? formatTime(slot.rightResult) : ''}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

const FinalsTable: React.FC<{
  matches: FinalsMatch[];
  competitors: Competitor[];
  title: string;
  headerColor: string;
}> = ({ matches, competitors, title, headerColor }) => (
  <div className="mb-2">
    <h3 className="text-base font-semibold text-gray-600 uppercase tracking-wider mb-1 px-1">{title}</h3>
    <div className="overflow-x-auto">
      <table className="w-full text-base border-collapse">
        <thead>
          <tr className="bg-gray-100 text-gray-500 text-xs uppercase tracking-wide">
            <th className="px-4 py-2 text-left w-40">Time</th>
            <th className="px-4 py-2 text-left w-32 hidden md:table-cell">Match</th>
            <th className="px-4 py-2 text-left">Athlete 1</th>
            <th className="px-4 py-2 text-center w-28">Time</th>
            <th className="px-4 py-2 text-center w-8 text-gray-300">vs</th>
            <th className="px-4 py-2 text-left">Athlete 2</th>
            <th className="px-4 py-2 text-center w-28">Time</th>
            <th className="px-4 py-2 text-center w-24">→</th>
          </tr>
        </thead>
        <tbody>
          {matches.map((match, i) => {
            const done   = match.leftResult.time !== null  || match.leftResult.dnf ||
                           match.rightResult.time !== null || match.rightResult.dnf;
            const winner = getMatchWinner(match);
            const left1  = getName(match.leftId,  competitors);
            const left2  = getName(match.rightId, competitors);
            return (
              <tr key={i} className={`border-b border-gray-100 ${done ? 'bg-green-50' : 'bg-white'} hover:bg-gray-50`}>
                <td className="px-4 py-3 font-mono text-sm text-gray-500 whitespace-nowrap">{match.timeRange}</td>
                <td className="px-4 py-3 text-xs text-gray-400 hidden md:table-cell">{match.matchLabel}</td>
                <td className={`px-4 py-3 font-semibold ${winner === match.leftId ? 'text-primary' : 'text-dark'}`}>
                  {left1 ? (
                    ATHLETE_BY_NAME[left1]?.id
                      ? <Link to={`/athletes/${ATHLETE_BY_NAME[left1].id}`} className="hover:underline transition-colors">{left1}</Link>
                      : left1
                  ) : <span className="text-gray-400 italic text-sm">{match.matchLabel.split('–')[0]?.trim()}</span>}
                </td>
                <td className="px-4 py-3 text-center font-mono font-bold text-primary">
                  {left1 ? formatTime(match.leftResult) : ''}
                </td>
                <td className="px-4 py-3 text-center text-gray-300 text-sm">vs</td>
                <td className={`px-4 py-3 font-semibold ${winner === match.rightId ? 'text-primary' : 'text-dark'}`}>
                  {left2 ? (
                    ATHLETE_BY_NAME[left2]?.id
                      ? <Link to={`/athletes/${ATHLETE_BY_NAME[left2].id}`} className="hover:underline transition-colors">{left2}</Link>
                      : left2
                  ) : <span className="text-gray-400 italic text-sm">{match.matchLabel.split('–')[1]?.trim()}</span>}
                </td>
                <td className="px-4 py-3 text-center font-mono font-bold text-primary">
                  {left2 ? formatTime(match.rightResult) : ''}
                </td>
                <td className="px-4 py-3 text-center text-xs font-semibold text-secondary">{match.resultLabel}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

// ─── SpeedSchedulePage ────────────────────────────────────────────────────────

const SpeedSchedulePage: React.FC = () => {
  const navigate                    = useNavigate();
  const { competitionId = 'bk2026' } = useParams<{ competitionId: string }>();
  const [data, setData]             = useState<SpeedCompetitionData | null>(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [connError, setConnError]   = useState(false);

  useEffect(() => {
    const unsub = subscribeSpeedData(
      competitionId,
      raw => {
        setData(autoPopulateBracket(raw));
        setLastUpdate(new Date());
        setConnError(false);
      },
      () => setConnError(true),
    );
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId]);

  if (!data) {
    if (connError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-dark text-white text-center px-4">
          <div>
            <div className="text-4xl mb-4">⚠</div>
            <p className="text-xl font-semibold">Could not connect to Firestore</p>
            <p className="text-gray-400 mt-2 text-sm">Check your internet connection or Firestore security rules.</p>
            <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-secondary rounded text-sm">
              Retry
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark text-white text-2xl">
        Loading…
      </div>
    );
  }

  const { men: mensRankings, women: womensRankings } = computeGenderedRankings(data);
  const hasQualTime = mensRankings.some(r => r.bestTime !== null) || womensRankings.some(r => r.bestTime !== null);

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Hero header ────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-dark to-secondary text-white py-10 px-4">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(`/competition/${competitionId}`)} className="flex items-center gap-1.5 text-blue-300 hover:text-white text-sm mb-5 transition-colors">
            ← Competition hub
          </button>
        </div>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row md:items-end justify-between gap-2">
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">
              Speed Climbing
            </h1>
            <p className="text-blue-200 mt-1 text-lg">Competition Schedule & Results</p>
          </div>
          <div className="text-right text-xs text-blue-300">
            {connError ? (
              <span className="text-red-300">⚠ Connection error</span>
            ) : (
              <>
                <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse mr-1" />
                Live · updated {lastUpdate.toLocaleTimeString()}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-10">

        {/* ── Qualifications ─────────────────────────────────────────── */}
        <section>
          <div className="bg-secondary text-white px-5 py-3 rounded-t-lg">
            <h2 className="text-2xl font-bold">Qualifications</h2>
          </div>
          <div className="bg-white rounded-b-lg shadow divide-y divide-gray-100 overflow-hidden">
            <div className="p-4">
              <QualTable slots={data.qualRound1} competitors={data.competitors} title="Round 1" />
            </div>
            <div className="p-4">
              <QualTable slots={data.qualRound2} competitors={data.competitors} title="Round 2" />
            </div>
          </div>
        </section>

        {/* ── Rankings ───────────────────────────────────────────────── */}
        {hasQualTime && (
          <section>
            <div className="bg-secondary text-white px-5 py-3 rounded-t-lg">
              <h2 className="text-2xl font-bold">Qualification Rankings</h2>
            </div>
            <div className="bg-white rounded-b-lg shadow divide-y divide-gray-100 overflow-hidden">
              {[{ label: '♂ Men', rows: mensRankings, prefix: 'MT' }, { label: '♀ Women', rows: womensRankings, prefix: 'WT' }].map(({ label, rows, prefix }) => (
                <div key={label} className="p-4">
                  <h3 className="text-base font-semibold text-gray-600 uppercase tracking-wider mb-2">{label}</h3>
                  <table className="w-full text-base border-collapse">
                    <thead>
                      <tr className="bg-gray-100 text-gray-500 text-xs uppercase tracking-wide">
                        <th className="px-4 py-2 text-center w-16">Seed</th>
                        <th className="px-4 py-2 text-left">Athlete</th>
                        <th className="px-4 py-2 text-center w-16">Class</th>
                        <th className="px-4 py-2 text-center w-28">Run 1</th>
                        <th className="px-4 py-2 text-center w-28">Run 2</th>
                        <th className="px-4 py-2 text-center w-32">Best Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => (
                        <tr key={r.competitor.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3 text-center font-bold text-secondary">{prefix}{r.rank}</td>
                          <td className="px-4 py-3 font-semibold text-dark">{r.competitor.name}</td>
                          <td className="px-4 py-3 text-center text-xs text-gray-400">
                            {ATHLETE_BY_NAME[r.competitor.name]?.classId
                              ? <ClassBadge classId={ATHLETE_BY_NAME[r.competitor.name].classId} />
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-center font-mono text-gray-600">{formatTime(r.run1)}</td>
                          <td className="px-4 py-3 text-center font-mono text-gray-600">{formatTime(r.run2)}</td>
                          <td className="px-4 py-3 text-center font-mono font-extrabold text-primary text-lg">
                            {r.bestTime !== null ? `${r.bestTime.toFixed(2)}s` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Finals ─────────────────────────────────────────────────── */}
        <section>
          <div className="bg-dark text-white px-5 py-3 rounded-t-lg flex items-center gap-3">
            <span className="text-2xl">♂</span>
            <h2 className="text-2xl font-bold">Men's Finals</h2>
          </div>
          <div className="bg-white rounded-b-lg shadow divide-y divide-gray-100 overflow-hidden">
            <div className="p-4">
              <FinalsTable matches={data.mensQuarterFinals} competitors={data.competitors} title="Quarter Finals" headerColor="bg-secondary" />
            </div>
            <div className="p-4">
              <FinalsTable matches={data.mensSemiFinals}    competitors={data.competitors} title="Semi Finals"    headerColor="bg-primary"   />
            </div>
            <div className="p-4">
              <FinalsTable matches={data.mensFinals}        competitors={data.competitors} title="Finals"         headerColor="bg-dark"      />
            </div>
          </div>
        </section>

        <section>
          <div className="bg-primary text-white px-5 py-3 rounded-t-lg flex items-center gap-3">
            <span className="text-2xl">♀</span>
            <h2 className="text-2xl font-bold">Women's Finals</h2>
          </div>
          <div className="bg-white rounded-b-lg shadow divide-y divide-gray-100 overflow-hidden">
            <div className="p-4">
              <FinalsTable matches={data.womensSemiFinals} competitors={data.competitors} title="Semi Finals" headerColor="bg-primary" />
            </div>
            <div className="p-4">
              <FinalsTable matches={data.womensFinals}     competitors={data.competitors} title="Finals"      headerColor="bg-dark"   />
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default SpeedSchedulePage;
